import {
	ConcurrentExperience,
	UFOExperience,
	ExperiencePerformanceTypes,
	ExperienceTypes,
} from '@atlassian/ufo';

export type CustomData = {
	[key: string]: string | number | boolean | CustomData | undefined;
};

export enum UfoExperience {
	InvitePeopleRendered = 'invite-people-rendered',
	InviteFormSubmitted = 'invite-form-submitted',
	RecaptchaExecuted = 'recaptcha-executed',
	ThirdPartyResultsLoaded = 'third-party-results-loaded',
	ExternalUsersLoaded = 'external-users-loaded',
	ControlSettingsLoaded = 'control-settings-loaded',
	NoInvitePermissionRendered = 'no-invite-permission-rendered',
	InviteSettingsUpdated = 'invite-settings-updated',
	DomainSettingsUpdated = 'domain-settings-updated',
	parallelInviteRequestSubmitted = 'parallel-invite-request-submitted',
	TeamsInviteSectionExperience = 'teams-invite-section-experience',
	RecaptchaLoaded = 'recaptcha-loaded',
}

enum ExperienceComponent {
	InvitePeople = 'invite-people',
	InviteeList = 'invitee-list',
}

const buildExperience = (
	performanceType: ExperiencePerformanceTypes,
	component: string = ExperienceComponent.InvitePeople,
	experienceType: ExperienceTypes = ExperienceTypes.Load,
) => {
	return {
		type: experienceType,
		performanceType,
		platform: { component },
	};
};

type Timings = {
	key: string;
	startMark: string;
	endMark: string;
};

const createUFOTimings = (key: string): Timings => ({
	key,
	startMark: `${key}_start`,
	endMark: `${key}_end`,
});

export const invitePeopleRenderedTimings = {
	getAccountInfo: createUFOTimings('get_account_info'),
	getInviteCapabilities: createUFOTimings('get_invite_capabilities'),
	checkEligibility: createUFOTimings('check_eligibility'),
	fetchOrgSettings: createUFOTimings('fetch_org_settings'),
	fetchExistingIntegrations: createUFOTimings('fetch_existing_integrations'),
	initSlack: createUFOTimings('init_slack'),
	getUserPermission: createUFOTimings('get_user_permission'),
	getCollections: createUFOTimings('get_collections'),
} as const;

export const invitePeopleRendered = new UFOExperience(UfoExperience.InvitePeopleRendered, {
	...buildExperience(ExperiencePerformanceTypes.PageSegmentLoad),
	timings: Object.values(invitePeopleRenderedTimings),
});

export const inviteFormSubmittedTimings = {
	updateOpenInviteSettings: createUFOTimings('update_open_invites_settings'),
	updateDirectAccessSettings: createUFOTimings('update_direct_access_settings'),
	inviteUsersPromise: createUFOTimings('invite_users'),
	getRecaptchaToken: createUFOTimings('get_recaptcha_token'),
} as const;

export const inviteFormSubmitted = new UFOExperience(UfoExperience.InviteFormSubmitted, {
	...buildExperience(ExperiencePerformanceTypes.PageSegmentLoad),
	timings: Object.values(inviteFormSubmittedTimings),
});

export const thirdPartyResultsLoaded = new ConcurrentExperience(
	UfoExperience.ThirdPartyResultsLoaded,
	buildExperience(ExperiencePerformanceTypes.PageSegmentLoad, ExperienceComponent.InviteeList),
);

export const externalUsersLoaded = new ConcurrentExperience(
	UfoExperience.ExternalUsersLoaded,
	buildExperience(ExperiencePerformanceTypes.PageSegmentLoad, ExperienceComponent.InviteeList),
);

export const controlSettingsLoaded = new UFOExperience(
	UfoExperience.ControlSettingsLoaded,
	buildExperience(ExperiencePerformanceTypes.Custom),
);

export const domainsSettingsUpdated = new UFOExperience(
	UfoExperience.DomainSettingsUpdated,
	buildExperience(ExperiencePerformanceTypes.Custom),
);

export const inviteSettingsUpdated = new UFOExperience(
	UfoExperience.InviteSettingsUpdated,
	buildExperience(ExperiencePerformanceTypes.Custom),
);

export const parallelInviteRequestSubmitted = new UFOExperience(
	UfoExperience.parallelInviteRequestSubmitted,
	buildExperience(ExperiencePerformanceTypes.Custom),
);

export const recaptchaLoaded = new UFOExperience(
	UfoExperience.RecaptchaLoaded,
	buildExperience(ExperiencePerformanceTypes.PageSegmentLoad),
);

export const startUfoExperience = (
	ufoExperience: ConcurrentExperience,
	id: string,
	properties?: CustomData,
) => {
	const experience = ufoExperience.getInstance(id);
	experience.start();
	if (properties) {
		experience.addMetadata(properties);
	}
};

export const succeedUfoExperience = (
	ufoExperience: ConcurrentExperience,
	id: string,
	properties?: CustomData,
) => {
	const experience = ufoExperience.getInstance(id);
	experience.success({ metadata: properties });
};

export const failUfoExperience = (
	ufoExperience: ConcurrentExperience,
	id: string,
	properties?: CustomData,
) => {
	const experience = ufoExperience.getInstance(id);
	experience.failure({ metadata: properties });
};

export const abortUfoExperience = (
	ufoExperience: ConcurrentExperience,
	id: string,
	properties?: CustomData,
) => {
	const experience = ufoExperience.getInstance(id);
	experience.abort({ metadata: properties });
};

export const noInvitePermissionRenderedTimings = {
	getOpenInviteNote: createUFOTimings('get_open_invite_note'),
} as const;

export const noInvitePermissionRendered = new UFOExperience(
	UfoExperience.NoInvitePermissionRendered,
	{
		...buildExperience(ExperiencePerformanceTypes.PageLoad),
		timings: Object.values(invitePeopleRenderedTimings),
	},
);
